import React from 'react';
import Select from 'react-select';

const ProductFormModal = ({
  form,
  formError,
  formLoading,
  onClose,
  onSubmit,
  onChange,
  onCategoryChange,
  categoryOptions,
  onSpecChange,
  onAddSpec,
  onRemoveSpec,
  onVariationChange,
  onAddVariation,
  onRemoveVariation,
}) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-60 flex justify-center items-center z-50">
      <div className="bg-gray-800 rounded-lg w-full max-w-md relative text-white flex flex-col max-h-[90vh]">

        {/* Header */}
        <div className="flex justify-between items-center p-4 border-b border-gray-700">
          <h2 className="text-xl font-bold">{form.id ? 'Edit Product' : 'Add Product'}</h2>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-white text-2xl leading-none"
            aria-label="Close"
            type="button"
          >
            ×
          </button>
        </div>

        <form
          onSubmit={onSubmit}
          className="p-4 overflow-y-auto flex-1 space-y-4"
          style={{ minHeight: 0 }}
        >
          {formError && <div className="mb-2 text-red-400 font-semibold">{formError}</div>}

          <input
            type="text"
            name="title"
            placeholder="Title"
            value={form.title}
            onChange={onChange}
            className="w-full p-2 rounded bg-gray-700 text-white"
          />

          <div className="text-black">
            <Select
              isMulti
              options={categoryOptions}
              value={categoryOptions.filter((opt) =>
                form.categories.includes(opt.value)
              )}
              onChange={onCategoryChange}
              className="text-sm"
              classNamePrefix="select"
              placeholder="Select categories"
            />
          </div>

          <textarea
            name="shortDescription"
            placeholder="Short description"
            value={form.shortDescription}
            onChange={onChange}
            className="w-full p-2 rounded bg-gray-700 text-white resize-y"
            rows={2}
          />

          <textarea
            name="fullDescription"
            placeholder="Full description"
            value={form.fullDescription}
            onChange={onChange}
            className="w-full p-2 rounded bg-gray-700 text-white resize-y"
            rows={4}
          />

          {/* Specifications */}
          <div>
            <label className="font-semibold mb-2 block text-white">Specifications</label>
            {form.specifications.map((spec, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Name"
                  value={spec.key}
                  onChange={(e) => onSpecChange(idx, 'key', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/2"
                />
                <input
                  type="text"
                  placeholder="Value"
                  value={spec.value}
                  onChange={(e) => onSpecChange(idx, 'value', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/2"
                />
                <button
                  type="button"
                  onClick={() => onRemoveSpec(idx)}
                  className="bg-red-500 px-2 rounded text-white"
                  aria-label="Remove specification"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={onAddSpec}
              className="bg-green-600 px-3 py-1 rounded text-white"
            >
              + Add specification
            </button>
          </div>

          {/* Image upload */}
          <div className="space-y-2">
            <input
              type="url"
              name="image"
              placeholder="Image URL"
              value={form.image}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
            />
            <input
              type="file"
              accept="image/*"
              onChange={async (e) => {
                const file = e.target.files[0];
                if (!file) return;
                const formData = new FormData();
                formData.append("image", file);
                try {
                  const res = await fetch("/api/upload", { method: "POST", body: formData });
                  const data = await res.json();
                  if (data.url) {
                    const fullUrl = `${window.location.origin}${data.url}`;
                    onChange({ target: { name: "image", value: fullUrl } });
                  }
                } catch (err) {
                  console.error("Upload error:", err);
                  alert("Failed to upload image");
                }
              }}
              className="w-full p-2 rounded bg-gray-700 text-white"
            />
            {form.image && (
              <img src={form.image} alt="Preview" className="w-32 h-32 object-cover rounded" />
            )}
          </div>

          {/* Main fields */}
          <div className="flex gap-2">
            <input
              type="number"
              name="price"
              placeholder="Price"
              value={form.price}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
              min="0"
              step="0.01"
            />
            <input
              type="number"
              name="stock"
              placeholder="Stock (optional)"
              value={form.stock}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
              min="0"
            />
          </div>

          <div>
            <input
              type="number"
              name="discount"
              placeholder="Discount (%)"
              value={form.discount || ''}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
              min="0"
              max="100"
              step="1"
            />
          </div>

          <div className="flex gap-2">
            <input
              type="number"
              name="rating"
              placeholder="Rating (e.g., 4.8)"
              value={form.rating}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
              step="0.1"
              min="0"
              max="5"
            />
            <input
              type="number"
              name="reviews"
              placeholder="Number of reviews"
              value={form.reviews}
              onChange={onChange}
              className="w-full p-2 rounded bg-gray-700 text-white"
              min="0"
            />
          </div>

          {/* Variations */}
          <div>
            <label className="font-semibold mb-2 block text-white">Variations</label>
            {form.variations.map((v, idx) => (
              <div key={idx} className="flex gap-2 mb-2">
                <input
                  type="text"
                  placeholder="Variation name"
                  value={v.name}
                  onChange={e => onVariationChange(idx, 'name', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/3"
                />
                <input
                  type="number"
                  placeholder="Price"
                  value={v.price}
                  onChange={e => onVariationChange(idx, 'price', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/3"
                />
                <input
                  type="number"
                  placeholder="Stock (optional)"
                  value={v.stock}
                  onChange={e => onVariationChange(idx, 'stock', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/6"
                />
                <input
                  type="number"
                  placeholder="Discount (%)"
                  value={v.discount}
                  onChange={e => onVariationChange(idx, 'discount', e.target.value)}
                  className="p-2 rounded bg-gray-700 text-white w-1/6"
                />
                <button
                  type="button"
                  onClick={() => onRemoveVariation(idx)}
                  className="bg-red-500 px-2 rounded text-white"
                  aria-label="Remove variation"
                >
                  ×
                </button>
              </div>
            ))}
            <button
              type="button"
              onClick={onAddVariation}
              className="bg-green-600 px-3 py-1 rounded text-white"
            >
              + Add variation
            </button>
          </div>

          <button
            type="submit"
            disabled={formLoading}
            className="w-full bg-blue-600 hover:bg-blue-700 py-2 rounded font-semibold"
          >
            {formLoading ? 'Saving...' : form.id ? 'Save changes' : 'Add product'}
          </button>
        </form>
      </div>
    </div>
  );
};

export default ProductFormModal;
